<template>
	<div>
		<b-form-group label="Type" label-cols="3">
			<b-select v-model="mode" :options="[
				{ text: 'Une seule requête', value: 'single' },
				{ text: 'Plusieurs requêtes', value: 'multiple' }
			]" style="max-width:300px" />
		</b-form-group>

		<b-card
			v-for="(req, idx) in requests"
			:key="'api-req-' + idx"
			class="mb-3"
			:header="mode === 'multiple' ? ('Requête ' + (idx + 1)) : null">

			<template v-if="mode === 'multiple'" #header>
				<div class="d-flex justify-content-between align-items-center">
					<strong>Requête {{ idx + 1 }}</strong>
					<b-button size="sm" variant="danger" :disabled="requests.length === 1" @click="removeRequest(idx)">✕ Supprimer</b-button>
				</div>
			</template>

			<!-- ===== Général ===== -->
			<h6>Général</h6>
			<b-form-group label="Url" label-cols="3">
				<b-input v-model="req.uri" placeholder="https://..." />
			</b-form-group>
			<b-form-group label="Verbe" label-cols="3">
				<b-select v-model="req.method" :options="['get', 'post', 'put', 'patch', 'delete']" style="max-width:200px" />
			</b-form-group>
			<b-form-group label="Temps d'attente après la requête (ms)" label-cols="3">
				<b-input type="number" v-model.number="req.sleep" style="max-width:200px" />
			</b-form-group>
			<b-form-checkbox v-model="req.unique" class="mb-2">
				Requête unique (une seule fois, pas une par ligne de données)
			</b-form-checkbox>
			<b-form-checkbox v-model="req.options_template" class="mb-2">
				Activer les templates <code>${...}</code> dans les paramètres de la requête
			</b-form-checkbox>
			<b-form-checkbox v-model="req.black_white_lists_edit" class="mb-2">
				Appliquer les listes noire / blanche
			</b-form-checkbox>

			<!-- ===== Fichiers ===== -->
			<h6 class="mt-3">Fichiers</h6>
			<b-form-checkbox v-model="req.files" class="mb-2">
				Envoyer des fichiers avec la requête
			</b-form-checkbox>
			<b-form-group v-if="req.files" label="Colonne d'urls des fichiers à envoyer" label-cols="4">
				<b-input v-model="req.files_url" :list="'api-cols-' + id" />
			</b-form-group>
			<b-form-group v-if="req.files" label="Colonne des noms des fichiers envoyés" label-cols="4">
				<b-input v-model="req.column_file_name" :list="'api-cols-' + id" />
			</b-form-group>
			<b-form-checkbox v-model="req.response_file" class="mb-2">
				La réponse de l'api est un fichier
			</b-form-checkbox>

			<!-- ===== Résultats ===== -->
			<h6 class="mt-3">Résultats</h6>
			<b-form-group label="Colonne où stocker le résultat" label-cols="4">
				<b-input v-model="req.column_result" :list="'api-cols-' + id" />
			</b-form-group>
			<b-form-group label="Colonne où stocker l'erreur" label-cols="4">
				<b-input v-model="req.column_error" :list="'api-cols-' + id" />
			</b-form-group>
			<b-form-group label="Colonne du nom de fichier (si réponse fichier)" label-cols="4">
				<b-input v-model="req.column_name" :list="'api-cols-' + id" />
			</b-form-group>
			<b-form-group label="Colonne où stocker les headers de réponse" label-cols="4">
				<b-input v-model="req.column_headers" :list="'api-cols-' + id" />
			</b-form-group>
			<b-form-group label="Colonne où stocker le code de statut HTTP" label-cols="4">
				<b-input v-model="req.status" :list="'api-cols-' + id" />
			</b-form-group>

			<!-- ===== Paramètres de la requête ===== -->
			<h6 class="mt-3">Paramètres de la requête</h6>
			<b-tabs>
				<b-tab v-for="field in requestListFields" :key="field.key" :title="field.label">
					<small class="text-muted d-block mt-2 mb-1">Colonnes à inclure automatiquement (valeur = nom de colonne dans les données de l'étape précédente)</small>
					<b-table-simple small bordered>
						<b-tbody>
							<b-tr v-for="(col, ci) in req[field.key]" :key="'col' + ci">
								<b-td>
									<b-input size="sm" v-model="req[field.key][ci]" :list="'api-cols-' + id" />
								</b-td>
								<b-td style="width:40px">
									<b-button size="sm" variant="danger" @click="req[field.key].splice(ci, 1)">✕</b-button>
								</b-td>
							</b-tr>
						</b-tbody>
					</b-table-simple>
					<b-button size="sm" @click="req[field.key].push('')">+ Colonne</b-button>

					<small class="text-muted d-block mt-3 mb-1">Valeurs statiques (clé fixe / valeur fixe)</small>
					<b-table-simple small bordered>
						<b-tbody>
							<b-tr v-for="(pair, pi) in req[field.key + '_params_list']" :key="'sp' + pi">
								<b-td>
									<b-input size="sm" v-model="pair.key" placeholder="clé" />
								</b-td>
								<b-td>
									<b-input size="sm" v-model="pair.value" placeholder="valeur" />
								</b-td>
								<b-td style="width:40px">
									<b-button size="sm" variant="danger" @click="req[field.key + '_params_list'].splice(pi, 1)">✕</b-button>
								</b-td>
							</b-tr>
						</b-tbody>
					</b-table-simple>
					<b-button size="sm" @click="req[field.key + '_params_list'].push({ key: '', value: '' })">+ Valeur statique</b-button>
				</b-tab>
			</b-tabs>

			<!-- ===== Logs ===== -->
			<h6 class="mt-3">Logs</h6>
			<b-form-group label="Colonne d'identifiant (id)" label-cols="4">
				<b-input v-model="req.logs.id_value" :list="'api-cols-' + id" />
			</b-form-group>
			<b-form-group label="Nom de l'identifiant dans le log" label-cols="4">
				<b-input v-model="req.logs.id_name" />
			</b-form-group>
			<b-table-simple small bordered>
				<b-thead>
					<b-tr>
						<b-th>Nom</b-th>
						<b-th>Type</b-th>
						<b-th>Valeur / Colonne</b-th>
						<b-th></b-th>
					</b-tr>
				</b-thead>
				<b-tbody>
					<b-tr v-for="(log, li) in req.logs.data" :key="'log' + li">
						<b-td>
							<b-input size="sm" v-model="log.colonne" />
						</b-td>
						<b-td>
							<b-select size="sm" v-model="log.type" :options="[
								{ text: 'Valeur fixe', value: 'valeur' },
								{ text: 'Colonne', value: 'colonne' }
							]" />
						</b-td>
						<b-td>
							<b-input size="sm" v-model="log.valeur" :list="log.type === 'colonne' ? ('api-cols-' + id) : null" />
						</b-td>
						<b-td style="width:40px">
							<b-button size="sm" variant="danger" @click="req.logs.data.splice(li, 1)">✕</b-button>
						</b-td>
					</b-tr>
				</b-tbody>
			</b-table-simple>
			<b-button size="sm" @click="req.logs.data.push({ colonne: '', type: 'valeur', valeur: '' })">+ Log</b-button>

			<!-- ===== Scripts ===== -->
			<h6 class="mt-3">Scripts</h6>
			<b-tabs>
				<b-tab title="Avant traitement (une fois)">
					<ace_editor :code="req.code_before" mode="javascript" @update="(val) => req.code_before = val" />
				</b-tab>
				<b-tab title="Après traitement (une fois)">
					<ace_editor :code="req.code" mode="javascript" @update="(val) => req.code = val" />
				</b-tab>
				<b-tab title="Avant chaque appel (ligne)">
					<ace_editor :code="req.code_line_before" mode="javascript" @update="(val) => req.code_line_before = val" />
				</b-tab>
				<b-tab title="Après chaque appel (ligne)">
					<ace_editor :code="req.code_line" mode="javascript" @update="(val) => req.code_line = val" />
				</b-tab>
			</b-tabs>
		</b-card>

		<b-button v-if="mode === 'multiple'" class="mb-3" variant="outline-primary" @click="addRequest">+ Ajouter une requête</b-button>

		<datalist :id="'api-cols-' + id">
			<option v-for="col in columns_data" :key="col" :value="col">{{ col }}</option>
		</datalist>
	</div>
</template>

<script>
	import uniqueId from "lodash.uniqueid";
	import ace_editor from "../ace_editor.vue";

	export default {
		name: "api_step",
		components: { ace_editor },
		props: {
			data: { default: () => [] },
			files: { default: () => [] },
			client: { default: null },
			size: { default: 1000 },
			step_position: { default: 0 }
		},
		data() {
			return {
				id: uniqueId(""),
				mode: "single",
				requests: [this.default_request()],
				columns_data: [],
				requestListFields: [
					{ key: "body", label: "Corps (body)" },
					{ key: "qs", label: "Paramètres d'url (query string)" },
					{ key: "headers", label: "En-têtes (headers)" },
					{ key: "form", label: "Formulaire (form)" },
					{ key: "formData", label: "Formulaire multipart (formData)" }
				]
			};
		},
		watch: {
			data: {
				deep: true,
				immediate: true,
				handler() {
					if (this.data.length > 0)
						this.columns_data = Object.keys(this.data[0]);
				}
			}
		},
		methods: {
			default_request() {
				return {
					uri: "",
					method: "get",
					sleep: 0,
					unique: false,
					options_template: false,
					black_white_lists_edit: false,

					files: false,
					files_url: "",
					column_file_name: "",
					response_file: false,

					column_result: "result",
					column_error: "error",
					column_name: "result",
					column_headers: "",
					status: "",

					body: [], body_params_list: [],
					qs: [], qs_params_list: [],
					headers: [], headers_params_list: [],
					form: [], form_params_list: [],
					formData: [], form_data_params_list: [],

					logs: { id_name: "", id_value: "", data: [] },

					code_before: "",
					code: "",
					code_line_before: "",
					code_line: ""
				};
			},
			addRequest() {
				this.requests.push(this.default_request());
			},
			removeRequest(idx) {
				if (this.requests.length > 1)
					this.requests.splice(idx, 1);
			},
			list_to_obj(list) {
				var obj = {};
				(list || []).forEach((pair) => {
					if (pair.key)
						obj[pair.key] = pair.value;
				});
				return obj;
			},
			obj_to_list(obj) {
				if (!obj || typeof obj !== "object")
					return [];
				return Object.keys(obj).map((key) => ({ key, value: obj[key] }));
			},
			build_request_output(req) {
				return {
					uri: req.uri,
					method: req.method,
					sleep: req.sleep,
					unique: req.unique,
					options_template: req.options_template,
					black_white_lists_edit: req.black_white_lists_edit,

					files: req.files,
					files_url: req.files_url,
					column_file_name: req.column_file_name,
					response_file: req.response_file,

					column_result: req.column_result,
					column_error: req.column_error,
					column_name: req.column_name,
					column_headers: req.column_headers,
					status: req.status,

					body: req.body,
					body_params: this.list_to_obj(req.body_params_list),
					qs: req.qs,
					qs_params: this.list_to_obj(req.qs_params_list),
					headers: req.headers,
					headers_params: this.list_to_obj(req.headers_params_list),
					form: req.form,
					form_params: this.list_to_obj(req.form_params_list),
					formData: req.formData,
					form_data_params: this.list_to_obj(req.form_data_params_list),

					logs: req.logs,

					code_before: req.code_before,
					code: req.code,
					code_line_before: req.code_line_before,
					code_line: req.code_line
				};
			},
			parse_request(d) {
				if (!d) d = {};
				var req = this.default_request();

				req.uri = d.uri || "";
				req.method = d.method || "get";
				req.sleep = d.sleep !== undefined ? d.sleep : 0;
				req.unique = !!d.unique;
				req.options_template = !!d.options_template;
				req.black_white_lists_edit = !!d.black_white_lists_edit;

				req.files = !!d.files;
				req.files_url = d.files_url || "";
				req.column_file_name = d.column_file_name || "";
				req.response_file = !!d.response_file;

				req.column_result = d.column_result || "result";
				req.column_error = d.column_error || "error";
				req.column_name = d.column_name || "result";
				req.column_headers = d.column_headers || "";
				req.status = d.status || "";

				req.body = Array.isArray(d.body) ? d.body : [];
				req.body_params_list = this.obj_to_list(d.body_params);
				req.qs = Array.isArray(d.qs) ? d.qs : [];
				req.qs_params_list = this.obj_to_list(d.qs_params);
				req.headers = Array.isArray(d.headers) ? d.headers : [];
				req.headers_params_list = this.obj_to_list(d.headers_params);
				req.form = Array.isArray(d.form) ? d.form : [];
				req.form_params_list = this.obj_to_list(d.form_params);
				req.formData = Array.isArray(d.formData) ? d.formData : [];
				req.form_data_params_list = this.obj_to_list(d.form_data_params);

				req.logs = {
					id_name: (d.logs && d.logs.id_name) || "",
					id_value: (d.logs && d.logs.id_value) || "",
					data: (d.logs && Array.isArray(d.logs.data)) ? d.logs.data : []
				};

				req.code_before = d.code_before || "";
				req.code = d.code || "";
				req.code_line_before = d.code_line_before || "";
				req.code_line = d.code_line || "";

				return req;
			},
			save() {
				if (this.mode === "multiple")
					return this.requests.map((req) => this.build_request_output(req));
				return this.build_request_output(this.requests[0]);
			},
			async load(data) {
				if (Array.isArray(data)) {
					this.mode = "multiple";
					this.requests = data.length > 0 ? data.map((d) => this.parse_request(d)) : [this.default_request()];
				} else {
					this.mode = "single";
					this.requests = [this.parse_request(data)];
				}
			}
		}
	};
</script>

<style scoped>
</style>
